import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Clock, Calendar } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';
import { assetUrl } from '../lib/siteAssets';
import { usePageSeo } from '../lib/seo';
import { BlogPostView } from '../components/blog/BlogPostView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogList = () => {
  usePageSeo('blog', { title: 'Relocation Insights & Tips', description: 'Practical advice, personal stories and guides for Australians moving to Italy and Europe.' });
  const [posts, setPosts] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [loading, setLoading] = useState(true);

  const categories = ['All', 'Italy', 'Pet Relocation', 'Expat Life', 'Relocation Tips'];

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog`, {
          params: activeCategory !== 'All' ? { category: activeCategory } : {}
        });
        setPosts(response.data.posts);
      } catch (error) {
        console.error('Error fetching posts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, [activeCategory]);

  const fadeUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Header */}
      <section data-testid="blog-header" className="container-custom mb-12">
        <motion.div {...fadeUp} className="text-center">
          <span className="inline-block text-[#C5A059] text-sm font-medium tracking-wider uppercase mb-4">
            Blog
          </span>
          <h1 className="font-serif text-4xl md:text-5xl font-semibold text-[#1E1622] mb-4">
            Relocation Insights & Tips
          </h1>
          <p className="text-[#1E1622]/70 max-w-2xl mx-auto">
            Expert advice, personal stories, and practical guides for your international move.
          </p>
        </motion.div>
      </section>

      {/* Category Tabs */}
      <section data-testid="blog-filters" className="container-custom mb-12">
        <motion.div {...fadeUp}>
          <Tabs value={activeCategory} onValueChange={setActiveCategory} className="w-full">
            <TabsList className="w-full justify-start bg-[#F5EFE9] p-1 rounded-full overflow-x-auto flex-nowrap">
              {categories.map((category) => (
                <TabsTrigger
                  key={category}
                  value={category}
                  data-testid={`blog-filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                  className="rounded-full px-6 py-2 text-sm font-medium whitespace-nowrap data-[state=active]:bg-white data-[state=active]:text-[#3B164C] data-[state=active]:shadow-sm"
                >
                  {category}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </motion.div>
      </section>

      {/* Blog Posts Grid */}
      <section data-testid="blog-posts" className="container-custom">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-[#F5EFE9] rounded-2xl h-96 animate-pulse" />
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {posts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="card-hover group"
              >
                {/* Post Image */}
                <div className="relative h-56 overflow-hidden rounded-t-2xl">
                  <img
                    src={assetUrl(post.image)}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <span className="absolute bottom-4 left-4 bg-[#3B164C] text-white text-xs font-medium px-3 py-1 rounded-full">
                    {post.category}
                  </span>
                </div>

                {/* Post Details */}
                <div className="p-6">
                  <div className="flex items-center gap-4 text-xs text-[#1E1622]/50 mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      {formatDate(post.date)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      {post.read_time}
                    </span>
                  </div>
                  
                  <h2 className="font-serif text-xl font-semibold text-[#1E1622] mb-3 line-clamp-2 group-hover:text-[#3B164C] transition-colors">
                    {post.title}
                  </h2>
                  
                  <p className="text-sm text-[#1E1622]/70 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  
                  <Link
                    to={`/blog/${post.id}`}
                    data-testid={`blog-post-link-${post.id}`}
                    className="inline-flex items-center gap-2 text-[#3B164C] font-medium text-sm hover:gap-3 transition-all"
                  >
                    Read Article
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </motion.article>
            ))}
          </motion.div>
        )}
      </section>

      {/* Empty State */}
      {!loading && posts.length === 0 && (
        <div className="container-custom text-center py-20">
          <p className="text-[#1E1622]/70 mb-4">No posts found in this category.</p>
          <button
            onClick={() => setActiveCategory('All')}
            className="btn-secondary"
          >
            View All Posts
          </button>
        </div>
      )}

      {/* Newsletter CTA */}
      <section data-testid="blog-cta" className="container-custom mt-20">
        <motion.div 
          {...fadeUp}
          className="bg-[#F5EFE9] rounded-3xl p-8 md:p-12 text-center"
        >
          <h2 className="font-serif text-2xl md:text-3xl font-semibold text-[#1E1622] mb-4">
            Get relocation tips in your inbox
          </h2>
          <p className="text-[#1E1622]/70 max-w-xl mx-auto mb-6">
            Join our newsletter for expert advice, insider tips, and updates on new relocation guides.
          </p>
          <Link 
            to="/freebie" 
            data-testid="blog-newsletter-cta"
            className="btn-primary inline-flex items-center gap-2"
          >
            Subscribe for Free
            <ArrowRight size={18} />
          </Link>
        </motion.div>
      </section>
    </div>
  );
};

const BlogPage = () => {
  const { postId } = useParams();
  return postId ? <BlogPostView postId={postId} /> : <BlogList />;
};

export default BlogPage;
